import { useCallback, useRef, useSyncExternalStore } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { synthesizeSpeech } from "../api/ttsApi";
import { createSentenceChunker, type SentenceChunker } from "../lib/sentenceChunker";
import { ttsPlayer } from "../lib/ttsPlayer";
import { useVoiceEnabled } from "./useVoiceEnabled";
import { useAssistantProfile } from "./useAssistantProfile";

function isAbortError(err: unknown): boolean {
  // Checked by `.name` rather than `instanceof Error`/`instanceof
  // DOMException` -- DOMException does not subclass Error in every runtime
  // (notably not in Node/jsdom), so an instanceof-Error check alone misses a
  // real fetch abort rejection here.
  return Boolean(err) && typeof err === "object" && (err as { name?: unknown }).name === "AbortError";
}

export function useTts() {
  const { t, i18n } = useTranslation();
  const locale = (i18n.language || "en").split("-")[0];
  const { ttsEnabled } = useVoiceEnabled();
  const { data: profile } = useAssistantProfile();
  const voice = (profile?.tts_voice as string) || "";

  const cache = useRef<Map<string, Blob[]>>(new Map());
  const chunkerRef = useRef<SentenceChunker | null>(null);
  const liveIdRef = useRef<string | null>(null);
  // Serializes the live (C) path's synth calls so enqueue order matches
  // sentence order regardless of per-fetch latency, and so endUtterance only
  // fires after every pending segment has actually enqueued.
  const liveChainRef = useRef<Promise<void>>(Promise.resolve());
  // Aborts the in-flight /speak/ HTTP call(s) for the current utterance on
  // barge-in (stop()) — ttsPlayer.stop() only aborts the playback sink, not
  // the outbound fetch, which would otherwise keep running (and billing) to
  // completion.
  const controllerRef = useRef<AbortController | null>(null);
  // Guards against toasting "tts.failed" more than once per live utterance —
  // one segment failing shouldn't spam a toast per remaining segment.
  const liveErrorShownRef = useRef(false);

  const state = useSyncExternalStore(
    (cb) => ttsPlayer.subscribe(cb),
    () => ttsPlayer.getState(),
  );

  const synthAndEnqueue = useCallback(
    async (messageId: string, text: string, signal?: AbortSignal) => {
      const resp = await synthesizeSpeech(text, { voice, locale, stream: true, signal });
      // Tee the body so we can both play it and cache a blob for replay.
      const [playStream, cacheStream] = resp.body ? resp.body.tee() : [null, null];
      const playResp = playStream ? new Response(playStream, { headers: resp.headers }) : resp;
      ttsPlayer.enqueue(messageId, playResp);
      if (cacheStream) {
        const blob = await new Response(cacheStream).blob();
        const arr = cache.current.get(messageId) ?? [];
        arr.push(blob);
        cache.current.set(messageId, arr);
      }
    },
    [voice, locale],
  );

  const speak = useCallback(
    async (messageId: string, text: string) => {
      if (!ttsEnabled || !text.trim()) return;
      const controller = new AbortController();
      controllerRef.current = controller;

      try {
        ttsPlayer.startUtterance(messageId);

        // Replay from cache if we already generated this message's audio.
        const cached = cache.current.get(messageId);
        if (cached?.length) {
          for (const blob of cached) ttsPlayer.enqueue(messageId, new Response(blob));
          ttsPlayer.endUtterance(messageId);
          return;
        }

        const chunker = createSentenceChunker();
        const sentences = [...chunker.feed(text)];
        const tail = chunker.flush();
        if (tail) sentences.push(tail);
        for (const s of sentences) await synthAndEnqueue(messageId, s, controller.signal);
        ttsPlayer.endUtterance(messageId);
      } catch (err) {
        // The player must never stay wedged in "playing" — reset it on any
        // failure. An abort (barge-in/stop()) is an intentional cancel, not
        // a user-facing failure, so stay silent for it; anything else gets a
        // toast via the existing (already-translated, previously unused)
        // assistant.tts.failed key.
        ttsPlayer.stop();
        if (!isAbortError(err)) {
          toast.error(t("assistant.tts.failed"));
        }
      }
    },
    [ttsEnabled, synthAndEnqueue, t],
  );

  const beginLive = useCallback(
    (messageId: string) => {
      if (!ttsEnabled) return;
      controllerRef.current = new AbortController();
      liveErrorShownRef.current = false;
      liveChainRef.current = Promise.resolve();
      liveIdRef.current = messageId;
      chunkerRef.current = createSentenceChunker();
      ttsPlayer.startUtterance(messageId);
    },
    [ttsEnabled],
  );

  const handleLiveSegmentError = useCallback(
    (err: unknown) => {
      if (isAbortError(err) || liveErrorShownRef.current) return;
      liveErrorShownRef.current = true;
      toast.error(t("assistant.tts.failed"));
    },
    [t],
  );

  const feedLive = useCallback(
    (delta: string) => {
      const id = liveIdRef.current;
      const chunker = chunkerRef.current;
      if (!id || !chunker) return;
      const signal = controllerRef.current?.signal;
      for (const sentence of chunker.feed(delta)) {
        // Chain (don't fire concurrently) so enqueue order always matches
        // sentence order, even if a later sentence's fetch resolves first.
        liveChainRef.current = liveChainRef.current
          .then(() => synthAndEnqueue(id, sentence, signal))
          .catch(handleLiveSegmentError); // a failed segment is skipped, not fatal to the chain
      }
    },
    [synthAndEnqueue, handleLiveSegmentError],
  );

  const endLive = useCallback(() => {
    const id = liveIdRef.current;
    const chunker = chunkerRef.current;
    if (!id || !chunker) return;
    const signal = controllerRef.current?.signal;
    const tail = chunker.flush();
    if (tail) {
      liveChainRef.current = liveChainRef.current
        .then(() => synthAndEnqueue(id, tail, signal))
        .catch(handleLiveSegmentError);
    }
    // Defer endUtterance until every chained segment has actually enqueued —
    // calling it synchronously (before any enqueue) would flip the player
    // back to idle while synth is still in flight, and the real ttsPlayer
    // drops a late enqueue() for a no-longer-active utterance (stale guard).
    liveChainRef.current = liveChainRef.current
      .then(() => ttsPlayer.endUtterance(id))
      .catch(() => {});
    liveIdRef.current = null;
    chunkerRef.current = null;
  }, [synthAndEnqueue, handleLiveSegmentError]);

  const stop = useCallback(() => {
    liveIdRef.current = null;
    chunkerRef.current = null;
    liveChainRef.current = Promise.resolve();
    controllerRef.current?.abort();
    controllerRef.current = null;
    ttsPlayer.stop();
  }, []);

  return {
    speak,
    beginLive,
    feedLive,
    endLive,
    stop,
    ttsEnabled,
    speakingId: state.activeId,
    isSpeaking: (messageId: string) => state.activeId === messageId && state.status === "playing",
  };
}
